import { useCallback, useEffect, useMemo, useState } from "react";

import { createCustomer, deleteCustomer, getCustomers, updateCustomer } from "../../../api";
import { logError } from "../../../logging/loggingService";
import { getEmailError, getPhoneError } from "../../../utils/contactValidation";
import { getTaxNumberError } from "../../../utils/taxNumber";
import { getVatIdError } from "../../../utils/vatId";
import { buildCustomerPayload, emptyCustomer, normalizeCustomer, normalizeTaxData } from "./customerUtils";

const getCustomerGroup = (customer) => {
    const fullName = `${customer?.name || ""} ${customer?.lastname || ""}`.trim();
    const candidate = fullName || (customer?.companyName || "").trim();
    if (!candidate) {
        return "#";
    }
    const letter = candidate.slice(0, 1).toUpperCase();
    return /^[A-ZÄÖÜ]$/.test(letter) ? letter : "#";
};

export default function useCustomerManagement() {
    const [openDialog, setOpenDialog] = useState(false);
    const [customerList, setCustomerList] = useState([]);
    const [currentCustomer, setCurrentCustomer] = useState(emptyCustomer());
    const [editingIndex, setEditingIndex] = useState(null);
    const [deleteAsk, setDeleteAsk] = useState(null);

    const [query, setQuery] = useState("");
    const [sortBy, setSortBy] = useState("name-asc");

    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [toast, setToast] = useState({ open: false, msg: "", severity: "success" });
    const [fieldErrors, setFieldErrors] = useState({});
    const [taxErrors, setTaxErrors] = useState({});
    const [addressErrors, setAddressErrors] = useState({});
    const [bankErrors, setBankErrors] = useState({});

    const taxNumberError = useMemo(() => getTaxNumberError(currentCustomer?.taxData?.taxNumber), [
        currentCustomer?.taxData?.taxNumber,
    ]);

    const vatIdError = useMemo(
        () => getVatIdError(currentCustomer?.taxData?.ustId || currentCustomer?.taxData?.vatNumber),
        [currentCustomer?.taxData?.ustId, currentCustomer?.taxData?.vatNumber],
    );

    const resetFormState = useCallback(() => {
        setCurrentCustomer(emptyCustomer());
        setEditingIndex(null);
        setFieldErrors({});
        setTaxErrors({});
        setAddressErrors({});
        setBankErrors({});
    }, []);

    const fetchCustomers = useCallback(() => {
        setLoading(true);
        setErr(null);
        getCustomers()
            .then((res) => {
                const normalized = (res.data || []).map(normalizeCustomer);
                setCustomerList(normalized);
            })
            .catch((e) => {
                logError("Fehler beim Laden der Kunden", e, { component: "CustomerManagement" });
                setErr("Kunden konnten nicht geladen werden.");
            })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        fetchCustomers();
    }, [fetchCustomers]);

    const filteredSorted = useMemo(() => {
        const q = query.trim().toLowerCase();
        let list = customerList;

        if (q) {
            list = list.filter((customer) => {
                const full = `${customer?.name || ""} ${customer?.lastname || ""}`.toLowerCase();
                return (
                    full.includes(q) ||
                    (customer?.email || "").toLowerCase().includes(q) ||
                    (customer?.phone || "").toLowerCase().includes(q)
                );
            });
        }

        const byName = (a, b) =>
            (`${a?.name || ""} ${a?.lastname || ""}`).localeCompare(
                `${b?.name || ""} ${b?.lastname || ""}`,
                "de",
            );
        const byCreated = (a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0);

        switch (sortBy) {
            case "name-desc":
                return [...list].sort((a, b) => -byName(a, b));
            case "newest":
                return [...list].sort(byCreated);
            case "oldest":
                return [...list].sort((a, b) => -byCreated(a, b));
            case "name-asc":
            default:
                return [...list].sort(byName);
        }
    }, [customerList, query, sortBy]);

    const customerIndexById = useMemo(() => {
        const map = new Map();
        customerList.forEach((customer, index) => {
            if (customer?.id) {
                map.set(customer.id, index);
            }
        });
        return map;
    }, [customerList]);

    const groupedCustomers = useMemo(() => {
        const groups = new Map();
        filteredSorted.forEach((customer) => {
            const group = getCustomerGroup(customer);
            if (!groups.has(group)) {
                groups.set(group, []);
            }
            groups.get(group).push(customer);
        });

        const keys = Array.from(groups.keys()).sort((a, b) => {
            if (a === "#") return 1;
            if (b === "#") return -1;
            return a.localeCompare(b, "de");
        });

        return keys.map((key) => ({
            key,
            customers: groups.get(key),
        }));
    }, [filteredSorted]);

    const handleDialogOpen = useCallback(() => {
        resetFormState();
        setOpenDialog(true);
    }, [resetFormState]);

    const handleDialogClose = useCallback(() => {
        setOpenDialog(false);
        resetFormState();
    }, [resetFormState]);

    const handleEdit = useCallback(
        (index) => {
            setCurrentCustomer(normalizeCustomer(customerList[index]));
            setEditingIndex(index);
            setFieldErrors({});
            setTaxErrors({});
            setAddressErrors({});
            setBankErrors({});
            setOpenDialog(true);
        },
        [customerList]
    );

    const handleSave = useCallback(async () => {
        const payload = buildCustomerPayload(currentCustomer);
        const nameValue = (payload.name || "").trim();
        const lastnameValue = (payload.lastname || "").trim();
        const companyValue = (payload.companyName || "").trim();
        const hasCompany = companyValue || (!lastnameValue && nameValue);
        const hasPerson = nameValue && lastnameValue;
        if (!hasCompany && !hasPerson) {
            setFieldErrors({
                name: "Bitte Vorname oder Firmenname angeben.",
            });
            setToast({ open: true, msg: "Bitte Firmenname oder Vor- und Nachnamen angeben.", severity: "warning" });
            return;
        }
        const emailError = getEmailError(payload.email);
        const phoneError = getPhoneError(payload.phone);
        if (emailError || phoneError) {
            setFieldErrors({
                ...(emailError ? { email: emailError } : {}),
                ...(phoneError ? { phone: phoneError } : {}),
            });
            setToast({ open: true, msg: "Bitte prüfe die markierten Felder.", severity: "warning" });
            return;
        }
        if (taxNumberError) {
            setTaxErrors({ taxNumber: taxNumberError });
            setToast({ open: true, msg: "Bitte gültige Steuernummer eingeben.", severity: "warning" });
            return;
        }
        if (vatIdError) {
            setTaxErrors({ ustId: vatIdError, vatNumber: vatIdError });
            setToast({ open: true, msg: "Bitte gültige Umsatzsteuer-ID eingeben.", severity: "warning" });
            return;
        }
        setFieldErrors({});
        setTaxErrors({});
        setAddressErrors({});
        setBankErrors({});
        try {
            if (editingIndex !== null) {
                const res = await updateCustomer(payload.id, payload);
                setCustomerList((list) => {
                    const next = [...list];
                    next[editingIndex] = normalizeCustomer(res.data);
                    return next;
                });
                setToast({ open: true, msg: "Kunde aktualisiert.", severity: "success" });
            } else {
                const res = await createCustomer(payload);
                setCustomerList((list) => [...list, normalizeCustomer(res.data)]);
                setToast({ open: true, msg: "Kunde angelegt.", severity: "success" });
            }
            handleDialogClose();
        } catch (e) {
            logError("Fehler beim Speichern des Kunden", e, { component: "CustomerManagement" });
            const backendError = e?.response?.data;
            const details = Array.isArray(backendError?.details) ? backendError.details : null;
            if (details && details.length > 0) {
                const nextFieldErrors = {};
                const nextTaxErrors = {};
                const nextAddressErrors = {};
                const nextBankErrors = {};
                details.forEach((detail) => {
                    const rawField = detail?.field;
                    const message = detail?.message;
                    if (!rawField || !message) return;
                    const addressMatch = /^addresses\[(\d+)\]\.(.+)$/.exec(rawField);
                    if (addressMatch) {
                        const index = Number(addressMatch[1]);
                        const field = addressMatch[2];
                        nextAddressErrors[index] = {
                            ...(nextAddressErrors[index] || {}),
                            [field]: message,
                        };
                        return;
                    }
                    const bankMatch = /^bankConnections\[(\d+)\]\.(.+)$/.exec(rawField);
                    if (bankMatch) {
                        const index = Number(bankMatch[1]);
                        const field = bankMatch[2];
                        nextBankErrors[index] = {
                            ...(nextBankErrors[index] || {}),
                            [field]: message,
                        };
                        return;
                    }
                    if (rawField.startsWith("taxData.")) {
                        const field = rawField.split(".").pop();
                        if (field === "vatFormatValid") {
                            nextTaxErrors.ustId = message;
                            nextTaxErrors.vatNumber = message;
                        } else {
                            nextTaxErrors[field] = message;
                        }
                        return;
                    }
                    if (rawField === "nameOrCompanyProvided") {
                        nextFieldErrors.name = message;
                        return;
                    }
                    nextFieldErrors[rawField] = message;
                });
                setFieldErrors(nextFieldErrors);
                setTaxErrors(nextTaxErrors);
                setAddressErrors(nextAddressErrors);
                setBankErrors(nextBankErrors);
                setToast({
                    open: true,
                    msg: backendError?.message || "Bitte prüfe die markierten Felder.",
                    severity: "error",
                });
                return;
            }
            setToast({ open: true, msg: "Speichern fehlgeschlagen.", severity: "error" });
        }
    }, [
        currentCustomer,
        editingIndex,
        handleDialogClose,
        taxNumberError,
        vatIdError,
    ]);

    const handleDeleteAsk = useCallback((cust, index) => setDeleteAsk({ cust, index }), []);

    const handleDeleteConfirm = useCallback(async () => {
        if (!deleteAsk) return;
        const prev = customerList;
        setCustomerList((list) => list.filter((_, i) => i !== deleteAsk.index));
        try {
            await deleteCustomer(deleteAsk.cust.id);
            setToast({ open: true, msg: "Kunde gelöscht.", severity: "success" });
        } catch (e) {
            logError("Fehler beim Löschen des Kunden", e, { component: "CustomerManagement" });
            setToast({ open: true, msg: "Löschen fehlgeschlagen.", severity: "error" });
            setCustomerList(prev);
        } finally {
            setDeleteAsk(null);
        }
    }, [customerList, deleteAsk]);

    const clearFieldError = useCallback((field) => {
        setFieldErrors((prev) => {
            if (!prev[field]) return prev;
            const next = { ...prev };
            delete next[field];
            return next;
        });
    }, []);

    const handleCustomerFieldChange = useCallback(
        (field, value) => {
            setCurrentCustomer((state) => ({ ...state, [field]: value }));
            clearFieldError(field);
        },
        [clearFieldError]
    );

    const onAddressChange = useCallback(
        (addresses) => {
            setCurrentCustomer((state) => ({ ...state, addresses }));
            if (Object.keys(addressErrors).length > 0) {
                setAddressErrors({});
            }
        },
        [addressErrors]
    );

    const onBankChange = useCallback(
        (bankConnections) => {
            setCurrentCustomer((state) => ({ ...state, bankConnections }));
            if (Object.keys(bankErrors).length > 0) {
                setBankErrors({});
            }
        },
        [bankErrors]
    );

    const onTaxChange = useCallback((patch) => {
        setCurrentCustomer((state) => ({
            ...state,
            taxData: normalizeTaxData({ ...(state.taxData || {}), ...patch }),
        }));
        const fields = Object.keys(patch || {});
        if (fields.length > 0) {
            setTaxErrors((prev) => {
                const next = { ...prev };
                fields.forEach((field) => {
                    delete next[field];
                });
                return next;
            });
        }
    }, []);

    return {
        openDialog,
        customerList,
        currentCustomer,
        editingIndex,
        deleteAsk,
        query,
        sortBy,
        loading,
        err,
        toast,
        fieldErrors,
        taxErrors,
        addressErrors,
        bankErrors,
        taxNumberError,
        vatIdError,
        filteredSorted,
        groupedCustomers,
        customerIndexById,
        setQuery,
        setSortBy,
        setToast,
        setDeleteAsk,
        fetchCustomers,
        handleDialogOpen,
        handleDialogClose,
        handleEdit,
        handleSave,
        handleDeleteAsk,
        handleDeleteConfirm,
        handleCustomerFieldChange,
        onAddressChange,
        onBankChange,
        onTaxChange,
    };
}
